/**
 * A game of poker against bots: the loop that ties the hand engine, the bots and the
 * analytics together.
 *
 * Everything here runs where the player is — in their browser, not on a server. That
 * is a design constraint and not an accident. A hand of nine-handed hold'em is a few
 * hundred bot decisions and several thousand Monte Carlo trials, and paying a network
 * round trip and a database write for each of them would be slow, expensive, and — as
 * this project has already learned the hard way — capable of taking the whole product
 * down on a free-tier quota. The session posts ONE summary when it ends.
 *
 * The whole game is deterministic given its seed. The same seed replays the same
 * cards, the same shuffles and the same bot decisions, which is what makes a hand
 * history a replay rather than a story and what lets a bug be reproduced from a
 * report instead of guessed at.
 */
import { Hand, seatFrom } from "./hand";
import { shuffledDeck, isLegalDeck, seedBytes } from "./deck";
import { CryptoRng, deriveSeed, randomSeed, sha256, toHex, type CryptoSeed } from "./rng";
import { comboCode } from "./range";
import { decide, currentBet, preflopSeatsBehind, raisesOnStreet, type Decision } from "./bot/policy";
import {
  openingRead, narrow, callStrength, STRENGTH, emptyHistory,
  type SeatRead, type SeatHistory,
} from "./bot/reads";
import { castFor, type CastMember } from "./cast";
import { profileById, type BotProfile, type Tier } from "./bot/profile";
import { accumulate, emptyStats, type Stats } from "./stats";
import type { Action, Card, HandResult, SeatConfig, SeatState, Street, TableRules } from "./types";
import type { TableEvent } from "./events";
import { NO_COSMETICS, type Equipped, type SeatView, type TableState } from "./table-state";

export interface GameConfig {
  /** 2 to 9. */
  seats: number;
  /** Which seat the human occupies, or -1 for a bot-only table. */
  heroSeat: number;
  /** Profile id for each seat; the hero's entry is ignored. */
  botProfiles: string[];
  rules: TableRules;
  /** Starting stack in chips for every seat. */
  startingStack: number;
  /** Cash games rebuy a busted seat; tournaments do not. */
  format: "cash" | "tournament";
  /**
   * Put every seat back to the starting stack before each hand.
   *
   * A practice table wants a constant depth: the point of choosing 100 big blinds is
   * to practise 100-big-blind poker, and a bot that runs hot for ten hands is playing
   * a different game by hand twelve. Ignored for tournaments, where the changing stack
   * IS the game. The running profit in `stats.net` is unaffected either way — it is
   * accumulated per hand, not read off the stack.
   */
  resetStacks?: boolean;
  /** Seat the button on the first hand. Moves clockwise after that. */
  button: number;
  /**
   * The session seed: 256 bits, as raw bytes or hex.
   *
   * Every hand's seed is derived from it, so one value reproduces a whole session and
   * each hand's own seed reproduces that hand alone. Omit it and a fresh one is drawn
   * from the platform's cryptographic random source, which is what a real table does.
   */
  seed?: CryptoSeed;
  /** Monte Carlo trials per bot equity question. Lower on slower devices. */
  botTrials?: number;
  /**
   * Seats whose every action is additionally priced by the reference model, for the
   * leak report. "all" is for benchmarking only — it doubles the work per decision.
   *
   * The reference is the expert profile's pricing, which is a HEURISTIC and not a
   * solve. Everything built on this number has to say so.
   */
  gradeSeats?: number[] | "all";
}

export interface ActionRecord {
  seat: number;
  street: Street;
  action: Action;
  /** Chips this action put in. */
  amount: number;
  potBefore: number;
  /**
   * The reference model's view of this action: what it would have done and what the
   * difference is worth. Present only for graded seats.
   */
  graded?: { evLost: number; bestLabel: string; chosenLabel: string };
  /** Present for bot actions and for graded hero actions. */
  decision?: {
    label: string;
    why: string;
    evLost: number;
    /** Every option the model priced, best first. */
    priced: { label: string; ev: number }[];
  };
}

export interface HandRecord {
  index: number;
  button: number;
  /**
   * The 256-bit seed this hand was dealt from, as hex. Recomputes the deck exactly,
   * and every bot decision with it.
   */
  seed: string;
  /**
   * SHA-256 of the seed, fixed before the first card was dealt.
   *
   * Published up front and checkable afterwards: hash the revealed seed and you have
   * shown that the deck was decided before the hand was played, not in response to it.
   */
  commitment: string;
  rules: TableRules;
  stacksBefore: number[];
  holes: (Card[] | null)[];
  board: Card[];
  /**
   * The cards burned before the flop, turn and river.
   *
   * Recorded rather than dropped so that a revealed deck can be accounted for position
   * by position. Without them a player re-dealing from the seed finds cards on screen
   * that the record does not mention, which looks exactly like the thing they were
   * checking for.
   */
  burned: Card[];
  actions: ActionRecord[];
  result: HandResult;
  /** Seats that saw the flop, for the analytics. */
  sawFlop: number[];
  showdownSeats: number[];
}

export type GamePhase = "idle" | "awaiting_hero" | "hand_over" | "finished";

/**
 * One seat, after the fact: the person the player saw, and what they were playing.
 *
 * Produced only by `Game.reveal()`, only once the session is finished, and carried in
 * its own type so it can never be mistaken for something that belongs in `TableState`.
 * Everything in here is the answer key.
 */
export interface SeatReveal {
  seat: number;
  name: string;
  shortName: string;
  /** The archetype's own name — "Calling Station", "The Rock". */
  archetype: string;
  tier: Tier;
  blurb: string;
  /** How often this seat followed the solved chart, 0..1. Countable, not claimed. */
  chartAdherence: number;
}

export class Game {
  readonly config: GameConfig;
  readonly seats: SeatConfig[];
  readonly profiles: (BotProfile | null)[];
  /** Who each seat appears to be. Null for the hero. Parallel to `profiles`. */
  readonly cast: (CastMember | null)[];
  readonly history: SeatHistory[];

  stacks: number[];
  button: number;
  handIndex = 0;
  hand: Hand | null = null;
  phase: GamePhase = "idle";
  /**
   * Every hand played, newest last, CAPPED so a long session cannot eat the tab.
   *
   * Because it is capped, nothing may compute a session total by iterating it. The
   * running totals in `stats` are the record; this array is the replay buffer. The
   * first benchmark run made exactly that mistake and reported a VPIP of 225%.
   */
  hands: HandRecord[] = [];
  /** Running per-seat statistics, updated as each hand finishes. */
  readonly stats: Stats[];
  /** Actions in the hand in progress. */
  private actions: ActionRecord[] = [];
  private reads = new Map<number, SeatRead>();
  /** The session seed, as bytes. Every hand's randomness descends from this. */
  readonly seed: Uint8Array;
  /**
   * SHA-256 of the session seed, fixed when the table is created.
   *
   * The per-hand commitment already proves each deck was decided before that hand was
   * played. This proves something the per-hand ones cannot: that every hand of the
   * session descends from ONE value chosen before the first card, so no hand was
   * re-seeded part-way through a session that was going badly. It is published at
   * sit-down and the seed it commits to is revealed when the session ends.
   */
  readonly sessionCommitment: string;
  private readonly reference = profileById("expert");
  /** How much the acting seat owed, captured before the action is applied. */
  private owedBefore = 0;
  /** Raises already made on this street when the acting seat acted. */
  private raisesBefore = 0;
  /** The seed of the hand in progress, and the commitment published before it was dealt. */
  private handSeed: Uint8Array = new Uint8Array(32);
  /**
   * The action-event stream for the presentation layer, drained rather than read.
   *
   * Buffered instead of pushed through a callback so that the engine never calls out
   * to anything — a callback here would be an I/O edge in a class whose determinism
   * the fuzz and replay tests depend on. The driver drains after each step.
   */
  private events: TableEvent[] = [];
  /** Guards against `finishHand` running twice for one hand. */
  private handFinished = false;
  /** Where the button sat last hand, so its move can be an event. */
  private previousButton: number | null = null;
  private handCommitment = "";
  /** The bots' randomness for the hand in progress, derived from that hand's seed. */
  private handRng: CryptoRng | null = null;
  private stacksAtHandStart: number[] = [];
  private sawFlop = new Set<number>();
  private enteredPot = new Set<number>();
  private raisedPreflop = new Set<number>();

  /** Keep this many hands of replay. Older ones are summarised into the stats. */
  static readonly HISTORY_LIMIT = 300;

  constructor(config: GameConfig) {
    assertConfig(config);
    this.config = config;
    /**
     * A missing seed means a real shuffle, not a default one. `randomSeed` throws
     * rather than falling back to Math.random(): a generator that quietly degrades to
     * something predictable is worse than one that refuses, because the failure is
     * invisible in exactly the deployment where it matters.
     */
    this.seed = config.seed === undefined ? randomSeed() : seedBytes(config.seed);
    if (this.seed.length !== 32) {
      throw new Error(`a session seed is 32 bytes, not ${this.seed.length}`);
    }
    this.sessionCommitment = toHex(sha256(this.seed));
    this.stacks = new Array(config.seats).fill(config.startingStack);
    this.button = config.button;
    this.history = Array.from({ length: config.seats }, emptyHistory);
    this.stats = Array.from({ length: config.seats }, () => emptyStats(config.rules.bigBlind));

    /**
     * The seats carry PEOPLE, not archetypes.
     *
     * `profile.name` used to go straight into the seat box, so the table announced
     * "Calling Station" on seat four and handed the player the single thing this
     * product teaches them to work out for themselves. The archetype now stays in
     * `this.profiles`, which is server-side and never crosses into `tableState`.
     *
     * The cast is drawn from the session seed on its own stream, so the same seed
     * rebuilds the same table and no name is reliably any one archetype. See
     * `engine/cast.ts` for why the mapping is deliberately uncorrelated.
     */
    const cast = castFor(config.seats, this.seed);

    this.profiles = [];
    this.seats = [];
    this.cast = [];
    for (let i = 0; i < config.seats; i++) {
      const isHero = i === config.heroSeat;
      const profile = isHero ? null : profileById(config.botProfiles[i] ?? "tag");
      const who = cast[i]!;
      this.profiles.push(profile);
      this.cast.push(isHero ? null : who);
      this.seats.push({
        id: isHero ? "hero" : `bot${i}`,
        name: isHero ? "You" : who.name,
        shortName: isHero ? "You" : who.short,
        stack: config.startingStack,
        isBot: !isHero,
      });
    }
  }

  /* ------------------------------------------------------------- the loop --- */

  /** Seats with chips. A cash game tops a busted seat back up before the next hand. */
  private live(): number[] {
    return this.stacks.map((s, i) => (s > 0 ? i : -1)).filter((i) => i >= 0);
  }

  /**
   * Deal the next hand and run the bots up to the first point the human must act.
   * Returns the seat waiting on the human, or null when the hand needs no input.
   */
  startHand(): number | null {
    if (this.phase === "finished") throw new Error("the game is over");
    if (this.hand && !this.hand.done) throw new Error("a hand is already in progress");

    if (this.config.format === "cash") {
      const reset = this.config.resetStacks ?? false;
      for (let i = 0; i < this.stacks.length; i++) {
        if (reset || this.stacks[i]! <= 0) this.stacks[i] = this.config.startingStack;
      }
    }
    if (this.live().length < 2) { this.phase = "finished"; return null; }

    // Move the button to the next seat that still has chips.
    if (this.handIndex > 0) {
      const n = this.config.seats;
      for (let step = 1; step <= n; step++) {
        const k = (this.button + step) % n;
        if (this.stacks[k]! > 0) { this.button = k; break; }
      }
    }

    /**
     * The hand's seed, and the commitment to it, are fixed BEFORE a card is dealt.
     *
     * That ordering is the whole point of publishing the commitment: it proves the
     * deck was decided in advance and could not have been influenced by how the hand
     * was played. Deriving it from the session seed rather than drawing fresh entropy
     * is what lets one recorded value replay a whole session.
     */
    this.handSeed = deriveSeed(this.seed, `hand:${this.handIndex}`);
    this.handCommitment = toHex(sha256(this.handSeed));
    this.handRng = new CryptoRng(this.handSeed, "bots");

    const deck = shuffledDeck(this.handSeed);
    /**
     * Asserted on every deal. A shuffle bug does not usually produce an unusual order,
     * it duplicates a card — and a duplicated card is a broken game rather than an
     * unlucky one, so it must never reach a player.
     */
    if (!isLegalDeck(deck)) throw new Error("the shuffle did not produce a legal deck");
    const states: SeatState[] = this.seats.map((cfg, i) =>
      seatFrom({ ...cfg, stack: this.stacks[i]! }, this.stacks[i]!, this.stacks[i]! <= 0));

    this.stacksAtHandStart = [...this.stacks];
    this.handFinished = false;
    this.actions = [];
    this.sawFlop = new Set();
    this.enteredPot = new Set();
    this.raisedPreflop = new Set();
    this.hand = new Hand(states, { rules: this.config.rules, button: this.button, deck });
    this.seedReads();

    /**
     * The opening events, emitted in the order they physically happen at a table:
     * the button is set, blinds and antes go in, then cards come off the deck one at
     * a time in real deal order. The scene animates from this; it does not infer a
     * deal from a state diff.
     */
    const prev = this.previousButton;
    this.previousButton = this.button;
    this.events.push({
      t: "hand_start", hand: this.handIndex, button: this.button,
      commitment: this.handCommitment,
    });
    if (prev !== null && prev !== this.button) {
      this.events.push({ t: "button_move", from: prev, to: this.button });
    }
    for (let i = 0; i < this.hand.seats.length; i++) {
      const posted = this.stacksAtHandStart[i]! - this.hand.seats[i]!.stack;
      if (posted > 0) {
        const kind = i === this.hand.blindSeats.sb ? "sb" : i === this.hand.blindSeats.bb ? "bb" : "ante";
        this.events.push({
          t: "post", seat: i, kind, amount: posted, stackAfter: this.hand.seats[i]!.stack,
        });
      }
    }
    for (let round = 0; round < 2; round++) {
      for (const i of this.live()) this.events.push({ t: "deal_hole", seat: i, index: round });
    }
    if (this.config.heroSeat >= 0) {
      this.events.push({
        t: "hole_shown", seat: this.config.heroSeat,
        cards: [...this.hand.seats[this.config.heroSeat]!.hole],
      });
    }

    for (const i of this.live()) this.history[i]!.hands++;

    return this.run();
  }

  /**
   * Apply the human's action, then run the bots on to the next point of input.
   * Returns the seat waiting on the human, or null when the hand is over.
   */
  act(action: Action): number | null {
    const h = this.hand;
    if (!h || h.done) throw new Error("no hand is in progress");
    if (h.toAct !== this.config.heroSeat) throw new Error("it is not your turn");
    this.applyAction(this.config.heroSeat, action, undefined);
    return this.run();
  }

  /** Everything the hero may do right now, or an empty list when it is not their turn. */
  heroOptions(): Action[] {
    const h = this.hand;
    if (!h || h.done || h.toAct !== this.config.heroSeat) return [];
    return h.legalActions(this.config.heroSeat);
  }

  /**
   * Price the hero's options with the same model the bots use.
   *
   * This is what makes the leak report possible, and it is also why the report has to
   * be careful about what it claims: the judge is our own heuristic, not a solve. It
   * is labelled that way everywhere it is shown.
   */
  gradeHero(): Decision | null {
    const h = this.hand;
    if (!h || h.done || h.toAct !== this.config.heroSeat) return null;
    return decide({
      hand: h, seat: this.config.heroSeat, profile: this.reference,
      reads: this.reads, histories: this.history,
      rng: gradeRng(this.handSeed, this.config.heroSeat),
      trials: this.config.botTrials ?? 400,
    });
  }

  /**
   * Advance exactly one bot action, then stop.
   *
   * This is what the pacing layer drives: one action, animate it, one action, animate
   * it. `run()` below is the same thing in a loop and is what the fuzz tests, the
   * replay tests and the Instant speed setting use.
   *
   * Both paths draw from the same RNG in the same order, so a session stepped one
   * action at a time and the same session run flat out are byte-identical. That is
   * not an accident of the current code — there is a test that fails if it stops
   * being true, because a pacing layer that perturbed the deal would quietly break
   * the provably-fair claim.
   */
  stepOnce(): { kind: "acted"; seat: number } | { kind: "awaiting"; seat: number } | { kind: "hand_over" } {
    const h = this.hand;
    if (!h) return { kind: "hand_over" };
    if (h.done) { this.finishIfNeeded(); return { kind: "hand_over" }; }

    const seat = h.toAct;
    if (seat === null) { this.finishIfNeeded(); return { kind: "hand_over" }; }
    if (seat === this.config.heroSeat) {
      this.phase = "awaiting_hero";
      return { kind: "awaiting", seat };
    }

    const profile = this.profiles[seat];
    if (!profile) throw new Error(`seat ${seat} has no profile and is not the hero`);
    const d = decide({
      hand: h, seat, profile, reads: this.reads, histories: this.history,
      /**
       * The bots draw from the HAND's stream, not a session-long one, so a recorded
       * hand seed replays that hand's decisions as well as its cards. It used to be
       * a session stream, which meant a single hand could not be reproduced without
       * replaying every hand before it.
       */
      rng: () => this.handRng!.next(),
      trials: this.config.botTrials,
    });
    this.applyAction(seat, d.action, d);
    if (h.done) { this.finishIfNeeded(); return { kind: "hand_over" }; }
    return { kind: "acted", seat };
  }

  /** Drain the events produced since the last drain. */
  drainEvents(): TableEvent[] {
    const out = this.events;
    this.events = [];
    return out;
  }

  /**
   * The player has stood up. The session is over.
   *
   * A cash game never reaches "finished" on its own — busted seats rebuy, so the
   * table would run forever. Leaving IS the end of the session from the only
   * perspective that matters, and it is the point the session is saved and the
   * archetypes may be revealed. Without this, `reveal()` was reachable in tournaments
   * and unreachable in cash, which is most of the play.
   *
   * Idempotent: a double-click on "Leave table" is not an error.
   */
  endSession(): void {
    this.phase = "finished";
  }

  /**
   * Who each seat actually was, released only once the session is over.
   *
   * THE REVEAL. Hiding the archetypes is defensible only because the player finds out
   * afterwards — otherwise it is not a lesson, it is just a game that withheld
   * information and never said why. This is the moment the session becomes teaching:
   * "the seat you kept bluffing into was a calling station" is worth more than any
   * number on the report.
   *
   * It THROWS while the session is live rather than returning an empty array. A
   * caller that reaches for this mid-session has a bug, and the failure mode of a
   * silent empty result is that the bug ships and the reveal is quietly always blank.
   * A caller that legitimately does not know can ask `phase` first.
   */
  reveal(): SeatReveal[] {
    if (this.phase !== "finished") {
      throw new Error(
        "reveal() before the session is finished: the archetypes are the answer key, " +
        "and handing them over mid-session defeats the point of hiding them");
    }
    const out: SeatReveal[] = [];
    for (let i = 0; i < this.config.seats; i++) {
      const p = this.profiles[i];
      const who = this.cast[i];
      if (!p || !who) continue;                   // the hero has neither
      out.push({
        seat: i,
        name: who.name,
        shortName: who.short,
        archetype: p.name,
        tier: p.tier,
        blurb: p.blurb,
        /**
         * The measurable half, reported as the fact it is. `chartAdherence` is "plays
         * the solved chart this often", which can be verified by counting — unlike
         * `discipline`, which is a property of our own heuristic and would be a claim
         * dressed as a statistic.
         */
        chartAdherence: p.chartAdherence,
      });
    }
    return out;
  }

  private finishIfNeeded(): void {
    if (this.handFinished) return;
    this.handFinished = true;
    this.finishHand();
  }

  private run(): number | null {
    let guard = 0;
    for (;;) {
      if (++guard > 500) throw new Error("hand did not terminate: this is a bug in the engine");
      const r = this.stepOnce();
      if (r.kind === "awaiting") return r.seat;
      if (r.kind === "hand_over") return null;
    }
  }

  private isGraded(seat: number): boolean {
    const g = this.config.gradeSeats;
    if (g === "all") return true;
    if (!g) return seat === this.config.heroSeat && this.config.heroSeat >= 0;
    return g.includes(seat);
  }

  /**
   * Price a seat's options with the reference model, before the action is applied.
   *
   * Must run BEFORE `hand.apply`, because afterwards the state it would price is gone —
   * the pot has changed, the seat may be all-in, and the question "what were the
   * options" no longer has an answer.
   */
  private gradeNow(seat: number, action: Action): ActionRecord["graded"] {
    const h = this.hand!;
    const d = decide({
      hand: h, seat, profile: this.reference, reads: this.reads, histories: this.history,
      rng: gradeRng(this.handSeed, seat),
      trials: this.config.botTrials ?? 200,
    });
    const chosen = matchPriced(d, action);
    return {
      evLost: Math.max(0, d.best.ev - (chosen?.ev ?? d.best.ev)),
      bestLabel: d.best.label,
      chosenLabel: chosen?.label ?? describeAction(action),
    };
  }

  private applyAction(seat: number, action: Action, decision: Decision | undefined): void {
    const h = this.hand!;
    const before = h.potSize();
    const graded = this.isGraded(seat) ? this.gradeNow(seat, action) : undefined;
    /**
     * Measured against `contributed`, the whole-hand total, and NOT against
     * `committed`, the current round's.
     *
     * An action that closes the betting round resets every seat's `committed` to zero
     * before this line runs, so the round-based version reported a NEGATIVE amount for
     * the last action of every street. That is not a cosmetic bug: the stats read
     * `amount > 0` to decide whether a player voluntarily put money in, so a player
     * who called to close the preflop round was recorded as having folded to it.
     */
    const contributedBefore = h.seats[seat]!.contributed;
    const streetBefore = h.street;
    // Recorded before the action, because afterwards the seat has either matched the
    // bet or folded and "what were you facing" no longer has an answer.
    this.owedBefore = currentBet(h) - h.seats[seat]!.committed;
    /**
     * Raises already in when this seat acted — captured BEFORE the action, so a seat's
     * own raise does not count as a bet it faced. Preflop every raiser owes the blind,
     * so reading this after the fact scored every single open-raise as "faced a bet
     * and did not fold", which quietly pulled the fold-to-bet denominator up again by
     * the exact number of pots each seat opened.
     */
    this.raisesBefore = raisesOnStreet(h, h.street);
    const boardBefore = [...h.board];

    h.apply(seat, action);

    const amount = h.seats[seat]!.contributed - contributedBefore;
    this.actions.push({
      seat, street: streetBefore, action, amount, potBefore: before,
      graded,
      decision: decision && {
        label: decision.priced.find((p) => p.action === decision.action)?.label
          ?? decision.best.label,
        why: decision.why,
        evLost: decision.evLost,
        priced: decision.priced.map((p) => ({ label: p.label, ev: p.ev })),
      },
    });

    /**
     * `boardBefore`, not `h.board`.
     *
     * Applying an action that closes a betting round deals the next street's cards
     * before this line runs, so the read of a PREFLOP call was being narrowed by hand
     * strength on a flop that did not exist when the call was made. It fired on the
     * last caller of every street, every hand: a big blind that closed the action with
     * a wide range was instantly re-read as if it had seen A-K-Q of spades first.
     */
    this.events.push({
      t: "action", seat, action,
      stackAfter: h.seats[seat]!.stack, potAfter: h.potSize(),
    });
    if (h.board.length > boardBefore.length) {
      this.events.push({ t: "burn" });
      this.events.push({
        t: "board", street: h.street, cards: h.board.slice(boardBefore.length),
      });
    }

    this.observe(seat, action, streetBefore, amount, before, boardBefore);
    if (h.street !== streetBefore && h.street !== "preflop") {
      for (const i of h.seats.map((s, k) => (s.folded || s.sittingOut ? -1 : k)).filter((k) => k >= 0)) {
        this.sawFlop.add(i);
      }
    }
  }

  /* ---------------------------------------------------------------- reads --- */

  private seedReads(): void {
    const h = this.hand!;
    this.reads.clear();
    for (let i = 0; i < h.seats.length; i++) {
      if (h.seats[i]!.sittingOut) continue;
      /**
       * Positional, not "how many seats are still in". At the moment reads are seeded
       * nobody has folded, so the live-seat count is n-1 for EVERY seat — which gave
       * the button, the blinds and under-the-gun the same assumed opening range and
       * made the whole positional read model inert.
       */
      const behind = preflopSeatsBehind(h, i);
      const { range, percent } = openingRead(behind, false, this.history[i]!, 1);
      this.reads.set(i, {
        range, base: range.clone(), openedPercent: percent, raises: 0, calls: 0, checks: 0,
      });
    }
  }

  private observe(
    seat: number, action: Action, street: Street, amount: number, potBefore: number,
    board: readonly Card[]
  ): void {
    const read = this.reads.get(seat);
    const hist = this.history[seat]!;

    if (street === "preflop") {
      /**
       * Any preflop action that puts chips in is voluntary, full stop.
       *
       * The blinds are posted by the engine and never appear as actions, so there is
       * nothing here to exclude — but an earlier guard tried to, and its condition
       * (`pot <= 1.5 big blinds`) is exactly true for the FIRST limper and for the
       * small blind completing, which are the two most common voluntary actions in
       * poker. Loose-passive seats were recorded ten points tighter than they played,
       * and that number is what every bot's read of them is built from.
       */
      if (amount > 0 && action.kind !== "fold" && action.kind !== "check") {
        if (!this.enteredPot.has(seat)) { this.enteredPot.add(seat); hist.vpip++; }
      }
      if ((action.kind === "raise" || action.kind === "bet" || action.kind === "allin")
          && !this.raisedPreflop.has(seat)) {
        this.raisedPreflop.add(seat); hist.pfr++;
      }
    }

    /**
     * Fold-to-bet, which is the number the bots use to work out whether bluffing this
     * opponent is worth anything. Counted only when the seat was genuinely facing a
     * bet — a fold in an unraised pot is not a fold to anything — and counting EVERY
     * response, not only the folds and the calls: raising is also not folding, and
     * leaving raises out of the denominator overstates how often a player folds.
     */
    /**
     * The blinds are not a bet.
     *
     * `owedBefore > 0` is true for every non-blind seat on every preflop action, so
     * counting it made this statistic "how often does this seat fold before the flop"
     * — around 80% for a solid profile, against a real fold-to-bet of 45-55%. That
     * number feeds `foldEquity`, so every bot with hand-reading skill was inflating
     * its estimated fold equity by up to 1.8x and bluffing far too much. The expert
     * profile, which weights it fully, is also the model that grades the player.
     */
    const facingRealBet = this.owedBefore > 0 && this.raisesBefore > 0;
    if (facingRealBet) {
      hist.betsFaced++;
      if (action.kind === "fold") hist.foldsToBet++;
    }

    if (!read) return;
    if (action.kind === "fold") { this.reads.delete(seat); return; }

    const potFraction = potBefore > 0 ? amount / potBefore : 0;
    const strength =
      action.kind === "check" ? STRENGTH.check
      : action.kind === "call" ? callStrength(potFraction)
      : action.kind === "allin" ? STRENGTH.reraise
      : potFraction > 0.9 ? STRENGTH.reraise : STRENGTH.bet;

    if (action.kind === "check") read.checks++;
    else if (action.kind === "call") read.calls++;
    else read.raises++;

    /**
     * Narrowed at FULL skill, always. The read stored here is what the actions imply;
     * how much of it a given bot uses is decided when it reads it, by `perceived`.
     * Narrowing with one arbitrary profile's skill — which is what this did, using
     * whichever bot happened to occupy the first seat — gave every player at the table
     * the same belief and made the hand-reading dial do nothing.
     */
    read.range = narrow({ range: read.range, street, board, strength, handReading: 1 });
    if (street === "preflop" && (action.kind === "raise" || action.kind === "bet")) {
      read.openedPercent *= 0.6;
    }
  }

  /* ------------------------------------------------------------- finishing --- */

  private finishHand(): void {
    const h = this.hand!;
    const result = h.result!;
    for (let i = 0; i < this.stacks.length; i++) this.stacks[i] = h.seats[i]!.stack;

    const showdownSeats = result.showdown
      ? h.seats.map((s, i) => (!s.folded && !s.sittingOut ? i : -1)).filter((i) => i >= 0)
      : [];

    this.hands.push({
      index: this.handIndex,
      button: this.button,
      seed: toHex(this.handSeed),
      commitment: this.handCommitment,
      rules: this.config.rules,
      stacksBefore: this.stacksAtHandStart,
      /**
       * Hole cards are recorded for the hero always, for anyone who reached showdown,
       * and for nobody else. A replay that shows what everyone folded is a replay that
       * teaches the wrong lesson, because at the table you never get to see it.
       */
      holes: h.seats.map((s, i) =>
        i === this.config.heroSeat || showdownSeats.includes(i) ? [...s.hole] : null),
      board: [...result.board],
      burned: [...h.burned],
      actions: this.actions,
      result,
      sawFlop: [...this.sawFlop],
      showdownSeats,
    });
    const record = this.hands[this.hands.length - 1]!;
    for (let i = 0; i < this.config.seats; i++) {
      if (record.stacksBefore[i]! > 0) accumulate(this.stats[i]!, record, i);
    }
    if (this.hands.length > Game.HISTORY_LIMIT) this.hands.shift();

    for (let i = 0; i < result.pots.length; i++) {
      const pot = result.pots[i]!;
      if (pot.winners.length === 0) continue;
      const share = pot.amount / pot.winners.length;
      for (const w of pot.winners) {
        this.events.push({ t: "pot_push", seat: w, amount: share, pot: i });
      }
    }
    for (const i of showdownSeats) {
      this.events.push({ t: "showdown", seat: i, cards: [...h.seats[i]!.hole] });
    }
    this.events.push({ t: "hand_end", net: [...result.net] });

    this.handIndex++;
    this.phase = this.live().length < 2 && this.config.format === "tournament"
      ? "finished" : "hand_over";
  }

  /* ------------------------------------------------------------ accessors --- */

  /** Net chips for a seat across the whole session, not just the replay buffer. */
  netFor(seat: number): number { return this.stats[seat]?.net ?? 0; }

  /** The most recent action of the hand in progress, for the felt's action bubble. */
  lastAction(): ActionRecord | null {
    return this.hand && !this.hand.done
      ? (this.actions[this.actions.length - 1] ?? null) : null;
  }

  /** The hand in progress as a plain object, for rendering. */
  view(): {
    street: Street; board: Card[]; pot: number; toAct: number | null;
    stacks: number[]; committed: number[]; folded: boolean[]; allIn: boolean[];
    heroHole: Card[] | null; button: number; currentBet: number;
  } | null {
    const h = this.hand;
    if (!h) return null;
    return {
      street: h.street,
      board: [...h.board],
      pot: h.potSize(),
      toAct: h.toAct,
      stacks: h.seats.map((s) => s.stack),
      committed: h.seats.map((s) => s.committed),
      folded: h.seats.map((s) => s.folded),
      allIn: h.seats.map((s) => s.allIn),
      heroHole: this.config.heroSeat >= 0 ? [...h.seats[this.config.heroSeat]!.hole] : null,
      button: this.button,
      currentBet: currentBet(h),
    };
  }

  /**
   * The table as one seat is entitled to see it.
   *
   * This is the ONLY thing the table scene is given. It is a plain-JSON snapshot, so
   * the same component renders whether this came from the engine in this tab or from
   * a socket — which is what makes multiplayer a transport change instead of a
   * rewrite. See `table-state.ts` for the rules this object obeys.
   *
   * `viewer` of -1 is a spectator: it sees no hole cards at all.
   *
   * `reveal` shows everyone's cards and is only ever set once a hand is over. It
   * reads the LIVE hand rather than the record on purpose — the record hides what
   * folders threw away, which is right for a replay you might share and wrong for the
   * "show me what I was up against" that follows your own hand.
   */
  tableState(
    viewer: number,
    opts: {
      reveal?: boolean;
      /**
       * Equipped cosmetics per seat, supplied by the caller.
       *
       * Passed in rather than looked up, because cosmetics live in the database and
       * `engine/` is deterministic and I/O-free — the property the fuzz and replay
       * tests rest on. The engine renders what it is handed and knows nothing about
       * where it came from.
       */
      equipped?: readonly (Equipped | null)[];
    } = {},
  ): TableState | null {
    const h = this.hand;
    if (!h) return null;
    const reveal = opts.reveal === true && h.done;

    const seats: SeatView[] = h.seats.map((s, i) => {
      const mine = i === viewer && viewer >= 0;
      const hole = mine || reveal ? [...s.hole] : null;
      const cfg = this.seats[i]!;
      return {
        index: i,
        /**
         * Name and portrait only. Which archetype this seat is playing is the answer
         * key to the skill the product sells, and it is not in this object at all.
         */
        identity: {
          name: cfg.name,
          shortName: cfg.shortName ?? cfg.name,
          avatarId: null,
        },
        stack: s.stack,
        committed: s.committed,
        folded: s.folded,
        allIn: s.allIn,
        sittingOut: s.sittingOut,
        isSelf: mine,
        equipped: opts.equipped?.[i] ?? NO_COSMETICS,
        hole: hole && hole.length > 0 ? hole : null,
      };
    });

    return {
      handNumber: this.handIndex,
      street: h.street,
      board: [...h.board],
      pot: h.potSize(),
      currentBet: currentBet(h),
      toAct: h.toAct,
      button: this.button,
      seats,
      rules: this.config.rules,
      commitment: this.handCommitment,
    };
  }

  /**
   * The hole cards of a seat in the hand that just finished, folded or not.
   *
   * The RECORD deliberately hides the cards of anyone who folded, because a replay you
   * might share should not show what people threw away. The screen you look at right
   * after your own hand is the one place that rule does not apply — "show me what I
   * was up against" is most of why playing a bot is worth doing — so it reads here.
   *
   * Returns null while a hand is in progress. Handing out live hole cards mid-hand
   * would put every opponent's cards one console command away.
   */
  liveHoles(seat: number): number[] | null {
    const h = this.hand;
    if (!h || !h.done) return null;
    const s = h.seats[seat];
    return s && s.hole.length === 2 && !s.sittingOut ? [...s.hole] : null;
  }

  /**
   * The session seed, in the form a player can paste into the verifier.
   *
   * Named for what it does. `seed` is bytes the engine uses; this is a disclosure, and
   * every call site should read like one.
   */
  revealSeed(): string {
    return toHex(this.seed);
  }

  /** What a bot currently believes a seat holds — for the "show me the read" panel. */
  readOf(seat: number): { percent: number; top: string[] } | null {
    const r = this.reads.get(seat);
    if (!r) return null;
    const ids = r.range.ids();
    const codes = new Map<string, number>();
    for (const id of ids) {
      const code = comboCode(id);
      codes.set(code, (codes.get(code) ?? 0) + r.range.weight(id));
    }
    const top = [...codes.entries()].sort((a, b) => b[1] - a[1]).slice(0, 12).map(([c]) => c);
    return { percent: (r.range.mass() / 1326) * 100, top };
  }
}

function assertConfig(c: GameConfig): void {
  if (!Number.isInteger(c.seats) || c.seats < 2 || c.seats > 9) {
    throw new Error(`a table seats 2 to 9 players, not ${c.seats}`);
  }
  if (c.heroSeat >= c.seats || c.heroSeat < -1) {
    throw new Error(`seat ${c.heroSeat} is not at a ${c.seats}-handed table`);
  }
  if (!(c.startingStack > 0)) throw new Error("a starting stack must be positive");
  if (!(c.rules.bigBlind > 0)) throw new Error("a big blind must be positive");
  if (c.rules.smallBlind > c.rules.bigBlind) throw new Error("the small blind is the smaller one");
  if (c.rules.ante < 0) throw new Error("an ante cannot be negative");
  if (!Number.isInteger(c.button) || c.button < 0 || c.button >= c.seats) {
    throw new Error(`the button must be a seat, not ${c.button}`);
  }
  for (let i = 0; i < c.seats; i++) {
    if (i === c.heroSeat) continue;
    profileById(c.botProfiles[i] ?? "tag");     // throws on an unknown id
  }
}

/**
 * Find the priced entry matching an action that was chosen elsewhere.
 *
 * Matched on kind and target rather than by identity, because the action being graded
 * came from a different call to `decide` — or from a human clicking a button — and is
 * not the same object.
 */
function matchPriced(d: Decision, action: Action): { label: string; ev: number } | null {
  let best: { label: string; ev: number; to: number } | null = null;
  for (const p of d.priced) {
    if (p.action.kind !== action.kind) continue;
    if (action.to === undefined || p.action.to === undefined) return { label: p.label, ev: p.ev };
    // Sizes are quantised differently by different callers; take the closest.
    const gap = Math.abs(p.action.to - action.to);
    if (!best || gap < Math.abs(best.to - action.to)) {
      best = { label: p.label, ev: p.ev, to: p.action.to };
    }
  }
  return best ? { label: best.label, ev: best.ev } : null;
}

function describeAction(a: Action): string {
  return a.to === undefined ? a.kind : `${a.kind} to ${a.to}`;
}

/**
 * The stream the reference model grades a seat's decision from.
 *
 * Its own label per seat, so grading never consumes the bots' randomness — a graded
 * hand and an ungraded one must deal and play identically, or the leak report would be
 * measuring a different hand from the one that happened.
 */
function gradeRng(handSeed: Uint8Array, seat: number): () => number {
  const r = new CryptoRng(handSeed, `grade:${seat}`);
  return () => r.next();
}
